import { Dispatch, Reducer } from 'react';
import { action, on, props, reducer, union } from 'ts-action';
import { VinSessionActionTypes } from './vin-session.actionTypes';
import type { EnrichedVehicleDetails, VinSessionState } from '../types';
import colors from '@packages/core/styles/colors';

export const vinSessionActions = {
    setSelectedVin: action(
        VinSessionActionTypes.setSelectedVin,
        props<{ selectedVin: EnrichedVehicleDetails | undefined }>()
    ),
    setIsVinLoading: action(VinSessionActionTypes.setIsVinLoading, props<{ isVinLoading: boolean }>()),
    setSelectedFilters: action(VinSessionActionTypes.setSelectedFilters, props<{ selectedFilters: string[] }>()),
    setTitle: action(VinSessionActionTypes.setTitle, props<{ title: string }>()),
    setVinBannerOpen: action(VinSessionActionTypes.setVinBannerOpen, props<{ vinBannerOpen: boolean }>()),
    setVinQmrsCount: action(
        VinSessionActionTypes.setVinQmrsCount,
        props<{
            vinQmrsCount: {
                qmrCount: number;
                nqrCount: number;
                escalationCount: number;
                investigationCount: number;
                totalCount: number;
            };
        }>()
    ),
    setPublicationsCount: action(VinSessionActionTypes.setPublicationsCount, props<{ publicationsCount: number }>()),
    setVinTechlineCasesCount: action(
        VinSessionActionTypes.setVinTechlineCasesCount,
        props<{ vinTechlineCasesCount: number }>()
    ),
    setToastMessage: action(
        VinSessionActionTypes.setToastMessage,
        props<{
            toast: {
                toastMessage: string;
                iconName: string;
                iconColor: keyof typeof colors;
                backgroundColor?: keyof typeof colors;
            };
        }>()
    ),
    setHasPublicationsCountFetched: action(VinSessionActionTypes.setHasPublicationsCountFetched),
    setHasQmrsCountFetched: action(VinSessionActionTypes.setHasQmrsCountFetched),
    setHasVinDataFetched: action(VinSessionActionTypes.setHasVinDataFetched),
    setSessionStarted: action(VinSessionActionTypes.setSessionStarted, props<{ startedAt: number }>()),
    setSessionEnded: action(VinSessionActionTypes.setSessionEnded),
    resetState: action(VinSessionActionTypes.resetState),
};

const actionsUnion = union(...Object.values(vinSessionActions));
type VinSessionActions = typeof actionsUnion.actions;

export type VinSessionActionDispatcher = Dispatch<VinSessionActions>;

export const initialState: VinSessionState = {
    selectedVin: undefined,
    isVinLoading: false,
    selectedFilters: [],
    title: 'Subaru TechShare',
    vinBannerOpen: false,
    vinQmrsCount: { qmrCount: 0, nqrCount: 0, escalationCount: 0, investigationCount: 0, totalCount: 0 },
    publicationsCount: 0,
    vinTechlineCasesCount: 0,
    toast: { toastMessage: '', iconName: '', iconColor: 'white', backgroundColor: 'black' },
    hasPublicationsCountFetched: false,
    hasQmrsCountFetched: false,
    hasVinDataFetched: false,
    sessionLifecycle: null,
};

export const vinSessionReducer: Reducer<VinSessionState, VinSessionActions> = reducer(
    initialState,
    on(vinSessionActions.setSelectedVin, (state, { selectedVin }) => {
        return {
            ...state,
            selectedVin: selectedVin,
        };
    }),
    on(vinSessionActions.setIsVinLoading, (state, { isVinLoading }) => {
        return {
            ...state,
            isVinLoading: isVinLoading,
        };
    }),
    on(vinSessionActions.setSelectedFilters, (state, { selectedFilters }) => {
        return {
            ...state,
            selectedFilters: selectedFilters,
        };
    }),
    on(vinSessionActions.setTitle, (state, { title }) => {
        return {
            ...state,
            title: title,
        };
    }),
    on(vinSessionActions.setVinBannerOpen, (state, { vinBannerOpen }) => {
        return {
            ...state,
            vinBannerOpen: vinBannerOpen,
        };
    }),
    on(vinSessionActions.setVinQmrsCount, (state, { vinQmrsCount }) => {
        return {
            ...state,
            vinQmrsCount,
        };
    }),
    on(vinSessionActions.setPublicationsCount, (state, { publicationsCount }) => {
        return {
            ...state,
            publicationsCount: publicationsCount,
        };
    }),
    on(vinSessionActions.setVinTechlineCasesCount, (state, { vinTechlineCasesCount }) => {
        return {
            ...state,
            vinTechlineCasesCount: vinTechlineCasesCount,
        };
    }),
    on(vinSessionActions.setToastMessage, (state, { toast }) => {
        return {
            ...state,
            toast: {
                ...state.toast,
                ...toast,
                backgroundColor: toast.backgroundColor ?? state.toast.backgroundColor ?? 'black',
            },
        };
    }),
    on(vinSessionActions.setHasPublicationsCountFetched, (state) => {
        return {
            ...state,
            hasPublicationsCountFetched: true,
        };
    }),
    on(vinSessionActions.setHasQmrsCountFetched, (state) => {
        return {
            ...state,
            hasQmrsCountFetched: true,
        };
    }),
    on(vinSessionActions.setHasVinDataFetched, (state) => {
        return {
            ...state,
            hasVinDataFetched: true,
        };
    }),
    on(vinSessionActions.setSessionStarted, (state, { startedAt }) => ({
        ...state,
        sessionLifecycle: { startedAt, hasEnded: false },
    })),
    on(vinSessionActions.setSessionEnded, (state) => ({
        ...state,
        sessionLifecycle: state.sessionLifecycle ? { ...state.sessionLifecycle, hasEnded: true } : null,
    })),
    on(vinSessionActions.resetState, () => initialState)
);
